import React from 'react';
import { Helmet } from 'react-helmet-async';

const SITE_URL = 'https://www.ranistore.ro';
const SITE_NAME = 'Rani Store';
const DEFAULT_IMAGE = 'https://customer-assets.emergentagent.com/job_841fbfa7-1a20-4a56-bfc0-aa4a083c4873/artifacts/mbzov694_Logo-Rani_final.webp';

/**
 * SEO Component for comprehensive search engine optimization
 * Includes: Meta tags, OpenGraph, Twitter Cards, JSON-LD Structured Data, Canonical URLs, hreflang
 */
export const SEO = ({
  title,
  description,
  keywords,
  image,
  url,
  type = 'website',
  language = 'ro',
  product = null,
  breadcrumbs = null,
  noindex = false,
}) => {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : SITE_NAME;
  const fullUrl = url ? `${SITE_URL}${url}` : SITE_URL;
  const fullImage = image || DEFAULT_IMAGE;
  const alternateLanguage = language === 'ro' ? 'en' : 'ro';

  // Organization Schema
  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Rani Store',
    legalName: 'S.C. RANISTORE S.R.L.',
    url: SITE_URL,
    logo: DEFAULT_IMAGE,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+40732901128',
      contactType: 'customer service',
      availableLanguage: ['Romanian', 'English'],
    },
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Str. Spiești, Nr. 51',
      addressLocality: 'București',
      addressRegion: 'Sector 3',
      postalCode: '',
      addressCountry: 'RO',
    },
    sameAs: [
      'https://www.facebook.com/ranistore',
      'https://www.instagram.com/ranistore',
    ],
  };

  // Website Schema
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${SITE_URL}/shop?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  };

  // Product Schema (if product data provided)
  const productSchema = product ? {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: product.description,
    image: product.images?.[0] || fullImage,
    sku: product.sku,
    brand: {
      '@type': 'Brand',
      name: 'Rani Store',
    },
    offers: {
      '@type': 'Offer',
      url: fullUrl,
      priceCurrency: 'RON',
      price: product.price,
      priceValidUntil: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      availability: product.stock > 0 
        ? 'https://schema.org/InStock' 
        : 'https://schema.org/OutOfStock',
      seller: {
        '@type': 'Organization',
        name: 'Rani Store',
      },
    },
    ...(product.rating && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: product.rating,
        reviewCount: product.reviewCount || 1,
      },
    }),
  } : null;

  // Breadcrumb Schema (if breadcrumbs provided)
  const breadcrumbSchema = breadcrumbs ? {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url ? `${SITE_URL}${item.url}` : undefined,
    })),
  } : null;

  // LocalBusiness Schema for better local SEO
  const localBusinessSchema = {
    '@context': 'https://schema.org',
    '@type': 'Store',
    name: 'Rani Store',
    image: DEFAULT_IMAGE,
    '@id': SITE_URL,
    url: SITE_URL,
    telephone: '+40732901128',
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Str. Spiești, Nr. 51',
      addressLocality: 'București',
      addressRegion: 'Sector 3',
      addressCountry: 'RO',
    },
    openingHoursSpecification: [
      {
        '@type': 'OpeningHoursSpecification',
        dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        opens: '09:00',
        closes: '18:00',
      },
    ],
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <html lang={language} />
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="author" content="Rani Store" />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      
      {/* Canonical URL */}
      <link rel="canonical" href={fullUrl} />
      
      {/* hreflang for language alternatives */}
      <link rel="alternate" hreflang={language} href={fullUrl} />
      <link rel="alternate" hreflang={alternateLanguage} href={`${fullUrl}${fullUrl.includes('?') ? '&' : '?'}lang=${alternateLanguage}`} />
      <link rel="alternate" hreflang="x-default" href={fullUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'product' ? 'product' : 'website'} />
      <meta property="og:url" content={fullUrl} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={fullImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={language === 'ro' ? 'ro_RO' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'ro' ? 'en_US' : 'ro_RO'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={fullUrl} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={fullImage} />

      {/* Product specific meta (for product pages) */}
      {product && <meta property="product:price:amount" content={product.price} />}
      {product && <meta property="product:price:currency" content="RON" />}
      {product && <meta property="product:availability" content={product.stock > 0 ? 'in stock' : 'out of stock'} />}
      {product && <meta property="product:condition" content="new" />}
      {product && <meta property="product:brand" content="Rani Store" />}

      {/* JSON-LD Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      {type !== 'product' && (
        <script type="application/ld+json">
          {JSON.stringify(localBusinessSchema)}
        </script>
      )}
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
